import { apiClient } from '@indipia/shared'
import type { OrderMode } from '../constants/orderSettings.logic'

// C-3 자재파트너배정·발주정보발송 / 상세창 자재파트너 카드 / H-5 공용 API (WP5 5단계 U0-1).
// 서버: 52-Planner Order* 컨트롤러. 날짜는 'YYYY-MM-DD', 일시는 오프셋 없는 'YYYY-MM-DDTHH:mm:ss'(서울 기준) 문자열 그대로 전달·보관한다.
// 요청 헤더(X-Comp-Id·X-User-Id)는 게이트웨이가 주입하므로 여기서 싣지 않는다.
// 쓰기 호출은 자동 재시도하지 않는다. 서버 message 원문은 보관·노출하지 않는다(코드로만 분기).

/** 조회 타임아웃(D-07 §3.2 #11) */
export const QUERY_TIMEOUT_MS = 5000
/** 한 곳 발송 타임아웃 */
export const SEND_TIMEOUT_MS = 30000
/** 일괄 발송(건별 순차 처리) 타임아웃 */
export const BATCH_TIMEOUT_MS = 120000

// ── 타입 ──────────────────────────────────────────────────────────────

export type OrderStatusCode =
  | 'DRAFT' | 'REPLY_WAIT' | 'REREPLY_WAIT' | 'RESEND_NEEDED' | 'CONFIRMED'
  | 'DECLINED' | 'SHIP_WAIT' | 'SHIP_REPLIED' | 'RECEIVED'

export interface OrderItemView { itemSeq: number; itemName: string; itemSpec: string | null; itemQty: number; itemUnit: string }

export interface OrderLastSend {
  orderSendId: number
  sendTypeCode: string
  attemptNo: number
  sentDtm: string | null
  sendStatus: string | null
  expiresDtm: string | null
  closedReasonCode: string | null
}

export interface OrderLastReply {
  replyResultCode: string
  replyDtm: string | null
  replyReasonCode: string | null
  availDate: string | null
  liftAvailYn: string | null
  replyNote: string | null
  replyActorId: number | null
}

export interface OrderDeliveryView {
  orderDeliveryId: number
  sentDtm: string | null
  replyDtm: string | null
  replyRound: number | null
  arrivalDate: string | null
  arrivalTime: string | null
  prevArrivalDate: string | null
  prevArrivalTime: string | null
  driverName: string | null
  driverPhone: string | null
  photoCnt: number | null
  issueYn: string | null
  issueNote: string | null
  replyActorId: number | null
}

export interface OrderReceiptView {
  orderReceiptId: number
  receiptDtm: string | null
  receiptDate: string | null
  receiptMethodCode: string | null
  photoCnt: number | null
  qtyCheckedCode: string | null
  issueNote: string | null
  receiptActorId: number | null
}

/** A1·A3·A4·A6 공통 `OrderVendorView` */
export interface OrderVendorRow {
  orderVendorId: number
  taskId: number
  scheduleId: number
  siteId: number
  vendorId: number
  vendorName: string
  orderStatusCode: OrderStatusCode | (string & {})
  /** AUTO | MANUAL | null(이행 행) */
  assignMethodCode: string | null
  assignReasonCode: string | null
  orderDate: string | null
  deliveryDate: string | null
  deliveryConfirmedYn: 'Y' | 'N' | (string & {})
  liftReqYn: 'Y' | 'N' | (string & {})
  deliveryAddress: string | null
  orderNote: string | null
  items: OrderItemView[]
  contentVer: number
  resendNeededYn: 'Y' | 'N' | (string & {})
  lastSend: OrderLastSend | null
  lastReply: OrderLastReply | null
  declinedBefore: boolean
  delivery: OrderDeliveryView | null
  receipt: OrderReceiptView | null
  warnings?: string[]
}

export interface ScheduleTaskOrderItem {
  taskId: number
  workTypeCode: string
  workTypeName: string
  taskStartDate: string
  taskEndDate: string
  orderDate: string | null
  deliveryDate: string | null
  vendorId: number | null
  vendorName: string | null
  deliveryConfirmedYn: 'Y' | 'N'
  /** 이 현장만 발주·시공 방식. null·없음이면 회사 설정(H-5)을 따른다 */
  orderModeOverride?: OrderMode | null
}

export interface OrderSettingItem {
  workTypeCode: string
  workTypeName: string
  orderYn: string | null
  orderLeadDays: number | null
  vendorId: number | null
  /** SIF-1 발주·시공 방식 — 'Y'=자재파트너가 시공까지 맡음(ORDER_YN 'Y' + CONSTRUCT 'Y'). 없거나 'N'이면 일반(별도발주/발주없음). */
  constructByVendorYn?: string | null
}

export interface VendorCandidate {
  vendorId: number
  vendorName: string
  mainVendor: boolean
  avgRating: number | null
  orderCount: number
  lastOrderDate: string | null
  deliveryRegion: string | null
  phone: string | null
  selected: boolean
  declinedBefore: boolean
  /** 서버가 내려줄 때만 표시(현재 없음) */
  replyWithinTwoHoursPercent?: number | null
  onTimePercent?: number | null
  liftAvailPercent?: number | null
}

export interface VendorCandidatesResult {
  taskId: number
  workTypeCode: string
  currentVendorId: number | null
  /** 서버 정렬 순서 그대로 — 재정렬 금지 */
  candidates: VendorCandidate[]
  evidence: { directOrder: boolean; reasonType: string | null; ruleVendorId: number | null; matchesCurrent: boolean }
}

export interface OrderPatchItem { itemName: string; itemSpec: string | null; itemQty: number; itemUnit: string }

/** A4 병합 패치 — 없는 필드(undefined)=변경 없음, 문자열 필드 null=비움, items 는 전체 교체(빈 배열=전부 삭제) */
export interface OrderPatch {
  expectedVer: number
  items?: OrderPatchItem[]
  liftReqYn?: 'Y' | 'N'
  deliveryAddress?: string | null
  orderNote?: string | null
}

export interface OrderScheduleAffected {
  orderVendorId: number
  vendorName: string
  fromStatusCode: string
  toStatusCode: string
  resendNeededYn: string
  shipLinkClosedYn: string
  staleDeliveryYn: string
}

export interface OrderScheduleResult {
  taskId: number
  orderDate: string | null
  deliveryDate: string | null
  affected: OrderScheduleAffected[]
  warnings: string[]
}

export interface OrderSendResult {
  orderSendId: number
  attemptNo: number
  sendTypeCode: string
  sendMethodCode: string
  orderStatusCode: string
  sentDtm: string | null
  expiresDtm: string | null
  closedSends: unknown[]
}

export interface OrderBatchItem {
  /** 요청한 orderVendorId */
  ref: number | string
  status: 'SUCCESS' | 'FAILED'
  code: string | null
  /** 서버 message 는 화면에 쓰지 않음 */
  message?: string | null
  data: unknown
}

export interface OrderBatchResult {
  batchId: string | number
  count: { requested: number; succeeded: number; failed: number }
  items: OrderBatchItem[]
}

/** GET /schedule/vendors 응답에서 이 화면이 쓰는 필드(연락처 조회용) */
export interface VendorInfoLite {
  vendorId: number
  vendorName: string
  vendorPhone: string | null
  /** 연락 방식(SIF-3) — Y=알림톡 발송-회신 / N=결과만 직접등록. 미제공이면 없음 */
  commYn?: string | null
}

/**
 * GET /schedule/vendors/metrics 한 행(SV2) — 거래 이력이 있는 거래처만 내려온다. % 는 분모 0 이면 null(0 이 아님).
 * deliveryTimelyPercent 는 이 화면에서 쓰지 않는다.
 */
export interface VendorMetric {
  vendorId: number
  tradeCount: number
  lastTradeDate: string | null
  replyWithinTwoHoursPercent: number | null
  onTimeDeliveryPercent: number | null
  liftAvailPercent: number | null
  deliveryTimelyPercent: number | null
}

/** A9 발송·회신 이력 한 건(최신순, 실패 행 포함) — GET /schedule/order-vendors/{id}/order-sends. 일시는 오프셋 없는 'YYYY-MM-DDTHH:mm:ss' */
export interface OrderSendHistoryItem {
  orderSendId: number
  attemptNo: number | null
  /** ORDER_REQUEST | ORDER_CHANGE | SHIP_REQUEST | ARRIVAL_NOTICE | ARRIVAL_CHANGE */
  sendTypeCode: string
  /** MANUAL | AUTO | OFFLINE(대행 입력) */
  sendMethodCode: string | null
  recipientName: string | null
  sentDtm: string | null
  sendStatus: string | null
  failReasonCode: string | null
  expiresDtm: string | null
  viewedDtm: string | null
  deliveryDate: string | null
  oldDeliveryDate: string | null
  itemSummary: string | null
  replyResultCode: string | null
  replyDtm: string | null
  replyReasonCode: string | null
  availDate: string | null
  liftAvailYn: string | null
  replyNote: string | null
  replyActorId: number | null
  closedReasonCode: string | null
  closedDtm: string | null
}

export interface OrderNoticePreview { kind: 'ORDER_REQUEST' | 'ORDER_CHANGE'; content: string; buttons: string[] }

// ── 오류 정규화 ──────────────────────────────────────────────────────

/** 네트워크 오류·타임아웃(응답 없음) 코드 */
export const ORDER_ERROR_NETWORK = 'NETWORK'
/** 응답은 왔지만 약속된 형태가 아님 */
export const ORDER_ERROR_BAD_RESPONSE = 'BAD_RESPONSE'
export const ORDER_ERROR_UNKNOWN = 'UNKNOWN'
/** 지난 공종(종료일이 오늘 이전)의 방식 변경을 서버가 거절(422) */
export const ORDER_ERROR_MODE_PAST_TASK = 'ORDER_MODE_PAST_TASK'

export class OrderApiError extends Error {
  code: string
  status: number | null
  field?: string
  reason?: string
  data?: unknown
  /** 서버 message 원문 — 서버가 직접 거절한 일부 코드(ORDER_MODE_PAST_TASK)에서만 화면에 쓴다 */
  serverMessage?: string
  constructor(code: string, status: number | null, extra: { field?: string; reason?: string; data?: unknown; serverMessage?: string } = {}) {
    super(code)   // 기본 문구는 코드로 정한다(서버 message 는 예외 코드에서만 serverMessage 로 읽는다)
    if (extra.serverMessage !== undefined) this.serverMessage = extra.serverMessage
    this.name = 'OrderApiError'
    this.code = code
    this.status = status
    if (extra.field !== undefined) this.field = extra.field
    if (extra.reason !== undefined) this.reason = extra.reason
    if (extra.data !== undefined) this.data = extra.data
  }
}

interface AxiosLikeError {
  response?: { status?: number; data?: { code?: unknown; message?: unknown; data?: unknown } | null }
}

/** axios 오류 → OrderApiError. 응답이 없으면 NETWORK(타임아웃 ECONNABORTED 포함). */
export function toOrderApiError(err: unknown): OrderApiError {
  if (err instanceof OrderApiError) return err
  const res = (err as AxiosLikeError | null | undefined)?.response
  if (!res) return new OrderApiError(ORDER_ERROR_NETWORK, null)
  const body = res.data
  const code = body && typeof body.code === 'string' && body.code ? body.code : ORDER_ERROR_UNKNOWN
  const inner = body?.data
  const extra: { field?: string; reason?: string; data?: unknown; serverMessage?: string } = {}
  if (code === ORDER_ERROR_MODE_PAST_TASK && typeof body?.message === 'string' && body.message) extra.serverMessage = body.message
  if (inner && typeof inner === 'object') {
    const o = inner as { field?: unknown; reason?: unknown }
    if (typeof o.field === 'string') extra.field = o.field
    if (typeof o.reason === 'string') extra.reason = o.reason
    extra.data = inner
  }
  return new OrderApiError(code, typeof res.status === 'number' ? res.status : null, extra)
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (e) {
    throw toOrderApiError(e)
  }
}

/** 현장별 방식 변경 응답에서 화면이 쓰는 값 */
export interface OrderModeChangeResult { orderInfoAlreadySent: boolean }

/** 응답 본문(표준 envelope) — apiClient 응답의 data */
interface Envelope<T> { data: T }

function need<T>(v: T | null | undefined): T {
  // 필수 응답 값이 없으면 기본값으로 채우지 않고 오류
  if (v == null) throw new OrderApiError(ORDER_ERROR_BAD_RESPONSE, null)
  return v
}

function needArray<T>(v: unknown): T[] {
  if (!Array.isArray(v)) throw new OrderApiError(ORDER_ERROR_BAD_RESPONSE, null)
  return v as T[]
}

// ── API ──────────────────────────────────────────────────────────────

export const orderApi = {
  /** Q1 스케줄의 모든 공종(직접발주 아님 포함) */
  getScheduleOrders(scheduleId: number, signal?: AbortSignal): Promise<ScheduleTaskOrderItem[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<{ orders: ScheduleTaskOrderItem[] }>>(
        `/schedule/schedules/${scheduleId}/orders`, { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<ScheduleTaskOrderItem>(need(res.data?.data)?.orders)
    })
  },

  /** A1 스케줄의 발주 거래처 행 */
  getOrderVendors(scheduleId: number, signal?: AbortSignal): Promise<OrderVendorRow[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<{ items: OrderVendorRow[] }>>(
        `/schedule/schedules/${scheduleId}/order-vendors`, { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<OrderVendorRow>(need(res.data?.data)?.items)
    })
  },

  /** Q3 발주 설정(공종별 직접발주 여부·리드타임·주거래처) */
  getOrderSettings(signal?: AbortSignal): Promise<OrderSettingItem[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<{ orderSettings: OrderSettingItem[] }>>(
        '/schedule/settings', { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<OrderSettingItem>(need(res.data?.data)?.orderSettings)
    })
  },

  /** Q4 거래처 목록(연락처 조회용) */
  getVendorDirectory(signal?: AbortSignal): Promise<VendorInfoLite[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<VendorInfoLite[]>>('/schedule/vendors', { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<VendorInfoLite>(res.data?.data)
    })
  },

  /** SV2 거래처별 지표(거래 이력이 있는 거래처만) */
  getVendorMetrics(signal?: AbortSignal): Promise<VendorMetric[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<VendorMetric[]>>('/schedule/vendors/metrics', { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<VendorMetric>(res.data?.data)
    })
  },

  /** V1 거래처 후보(서버 정렬 순서 그대로) */
  getVendorCandidates(taskId: number): Promise<VendorCandidatesResult> {
    return call(async () => {
      const res = await apiClient.get<Envelope<VendorCandidatesResult>>(
        `/schedule/tasks/${taskId}/vendor-candidates`, { timeout: QUERY_TIMEOUT_MS })
      const d = need(res.data?.data)
      needArray<VendorCandidate>(d.candidates)
      need(d.evidence)
      return d
    })
  },

  /** A3 거래처 지정 */
  assignVendor(taskId: number, vendorId: number): Promise<OrderVendorRow> {
    return call(async () => {
      const res = await apiClient.post<Envelope<OrderVendorRow>>(
        `/schedule/tasks/${taskId}/order-vendors`, { vendorId }, { timeout: QUERY_TIMEOUT_MS })
      return need(res.data?.data)
    })
  },

  /** A6 거래처 변경(이전 행 REPLACED, 새 행 DRAFT) */
  replaceVendor(orderVendorId: number, vendorId: number): Promise<{ replaced: OrderVendorRow; created: OrderVendorRow }> {
    return call(async () => {
      const res = await apiClient.post<Envelope<{ replaced: OrderVendorRow; created: OrderVendorRow }>>(
        `/schedule/order-vendors/${orderVendorId}/replace`, { vendorId }, { timeout: QUERY_TIMEOUT_MS })
      const d = need(res.data?.data)
      need(d.replaced); need(d.created)
      return d
    })
  },

  /** A5 지정 해제(발송 이력 없는 DRAFT 만) */
  releaseVendor(orderVendorId: number): Promise<void> {
    return call(async () => {
      await apiClient.delete(`/schedule/order-vendors/${orderVendorId}`, { timeout: QUERY_TIMEOUT_MS })
    })
  },

  /** A4 발주정보 병합 패치. 날짜는 싣지 않는다(날짜는 patchOrderSchedule). */
  patchOrder(orderVendorId: number, patch: OrderPatch): Promise<OrderVendorRow> {
    return call(async () => {
      const body: Record<string, unknown> = { expectedVer: patch.expectedVer }
      // undefined 필드는 보내지 않는다(없음=변경 없음). null·빈 배열은 그대로 보낸다.
      if (patch.items !== undefined) body.items = patch.items
      if (patch.liftReqYn !== undefined) body.liftReqYn = patch.liftReqYn
      if (patch.deliveryAddress !== undefined) body.deliveryAddress = patch.deliveryAddress
      if (patch.orderNote !== undefined) body.orderNote = patch.orderNote
      const res = await apiClient.patch<Envelope<OrderVendorRow>>(
        `/schedule/order-vendors/${orderVendorId}`, body, { timeout: QUERY_TIMEOUT_MS })
      return need(res.data?.data)
    })
  },

  /**
   * SIF-1 4-3 현장별 발주·시공 방식 변경('이 현장만'). 회사 기본값(H-5)과 별개로 이 task(현장·공종)만 바꾼다.
   * 전환 시 시공파트너 자동배정 편입/해제·일정확인 대상 편입은 서버가 처리한다(FE 는 성공 후 상세창 데이터 refetch 만).
   * 응답의 orderInfoAlreadySent=true 면 발주정보가 이미 거래처에 나간 이력이 있다는 뜻(새 조건으로 다시 보내야 함).
   * 종료일이 오늘 이전인 공종은 서버가 422 ORDER_MODE_PAST_TASK 로 거절한다.
   */
  patchTaskOrderMode(taskId: number, orderMode: OrderMode): Promise<OrderModeChangeResult> {
    return call(async () => {
      const res = await apiClient.patch<Envelope<{ orderInfoAlreadySent?: boolean } | null>>(
        `/schedule/tasks/${taskId}/order-mode`, { orderMode }, { timeout: QUERY_TIMEOUT_MS })
      return { orderInfoAlreadySent: res.data?.data?.orderInfoAlreadySent === true }
    })
  },

  /** C9 발주일·입고희망일 병합 패치(없음=그대로, null=비움) */
  patchOrderSchedule(taskId: number, patch: { orderDate?: string | null; deliveryDate?: string | null }): Promise<OrderScheduleResult> {
    return call(async () => {
      const body: Record<string, unknown> = {}
      if (patch.orderDate !== undefined) body.orderDate = patch.orderDate
      if (patch.deliveryDate !== undefined) body.deliveryDate = patch.deliveryDate
      const res = await apiClient.patch<Envelope<OrderScheduleResult>>(
        `/schedule/tasks/${taskId}/order-schedule`, body, { timeout: QUERY_TIMEOUT_MS })
      const d = need(res.data?.data)
      needArray(d.affected); needArray(d.warnings)
      return d
    })
  },

  /** A9 발송·회신 이력(최신순) — 세부 내역 팝업용 */
  getOrderSends(orderVendorId: number, signal?: AbortSignal): Promise<OrderSendHistoryItem[]> {
    return call(async () => {
      const res = await apiClient.get<Envelope<{ items: OrderSendHistoryItem[] }>>(
        `/schedule/order-vendors/${orderVendorId}/order-sends`, { signal, timeout: QUERY_TIMEOUT_MS })
      return needArray<OrderSendHistoryItem>(need(res.data?.data)?.items)
    })
  },

  /** A7 한 곳 발송(본문 없음) */
  sendOrder(orderVendorId: number): Promise<OrderSendResult> {
    return call(async () => {
      const res = await apiClient.post<Envelope<OrderSendResult>>(
        `/schedule/order-vendors/${orderVendorId}/order-sends`, undefined, { timeout: SEND_TIMEOUT_MS })
      return need(res.data?.data)
    })
  },

  /**
   * 결과만 직접등록(SIF-3) [발주 기록] — A10 담당자 대행 입력(가능 회신). 알림톡·링크 없이 OFFLINE 발송 행을 만들어 확정으로 기록한다.
   * POST /schedule/order-vendors/{id}/replies (서버 OrderReplyController)
   */
  recordOrder(orderVendorId: number): Promise<void> {
    return call(async () => {
      await apiClient.post(
        `/schedule/order-vendors/${orderVendorId}/replies`, { replyResultCode: 'ACCEPTED' }, { timeout: SEND_TIMEOUT_MS })
    })
  },

  /** A8 일괄 발송(1~50, 건별 독립·순차) */
  sendOrderBatch(orderVendorIds: number[]): Promise<OrderBatchResult> {
    return call(async () => {
      const res = await apiClient.post<Envelope<OrderBatchResult>>(
        '/schedule/order-sends/batch',
        { items: orderVendorIds.map(orderVendorId => ({ orderVendorId })) },
        { timeout: BATCH_TIMEOUT_MS })
      const d = need(res.data?.data)
      needArray(d.items); need(d.count)
      return d
    })
  },

  /**
   * 알림톡 미리보기 — GET /schedule/order-vendors/{orderVendorId}/notice-preview (서버 OrderNoticePreviewController).
   * 응답 data={kind, content, buttons[]}. 실패(외부 정보 조회 실패 포함)는 호출부가 오류 상태로 표시한다(조용히 숨기지 않음).
   */
  getOrderNoticePreview(orderVendorId: number): Promise<OrderNoticePreview> {
    return call(async () => {
      const res = await apiClient.get<Envelope<OrderNoticePreview>>(
        `/schedule/order-vendors/${orderVendorId}/notice-preview`, { timeout: QUERY_TIMEOUT_MS })
      const d = need(res.data?.data)
      if (typeof d.content !== 'string' || !Array.isArray(d.buttons)) throw new OrderApiError(ORDER_ERROR_BAD_RESPONSE, null)
      return d
    })
  },
}
